import React, { useState } from "react";
import { motion } from "framer-motion";
import customer from "../assets/customer.jpg";
import validator from "validator";

const fadeInVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "https://api.form.curaai.ca";

const Pricing = () => {
  const [messageSent, setMessageSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = e.target.name.value;
    const email = e.target.email.value;
    const clinic = e.target.clinic.value;

    if (!validator.isEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      setEmailInvalid(true);
      return;
    } else {
      setEmailInvalid(false);
    }

    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, clinic }),
      });

      const result = await response.json();

      if (response.ok) {
        setMessageSent(true);
        setErrorMessage("");
        e.target.reset();
        setTimeout(() => setMessageSent(false), 5000);
      } else {
        setErrorMessage(result.error || "Something went wrong.");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to send message. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen font-sans bg-white text-black px-6 md:px-16 pb-22 pt-18 gap-16 justify-center items-start">
      {/* Left: Image */}
      <div className="w-full md:w-1/2 order-2 md:order-1 mt-4 md:mt-[44px] flex flex-col items-center">
        <motion.div
          className="bg-white rounded-xl shadow-xl overflow-hidden w-full h-auto"
          variants={fadeInVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <img
            src={customer}
            alt="Cura AI"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <p className="text-sm text-gray-700 mt-4 text-center pt-4 px-4">
          Need help choosing a plan? Contact us at{" "}
          <a href="mailto:info@CuraAI.ca" className="text-[#427cfc] hover:underline">
            info@CuraAI.ca
          </a>
        </p>
      </div>

      {/* Right: Heading + Form */}
      <motion.div
        className="w-full md:w-1/2 flex flex-col justify-start order-1 md:order-2 max-w-md"
        variants={fadeInVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <h1 className="text-3xl md:text-4xl font-medium mb-2">
          Cura AI Pricing
        </h1>

        <p className="text-[#427dfcca] md:text-[1.05rem] font-meidum pt-1 mb-6">
          Please schedule a demo to get started
        </p>
        <p className="text-gray-600 md:text-[1.05rem] text-md mb-10">
          At Cura AI, we offer flexible pricing plans tailored to meet the needs
          of clinics and healthcare organizations. Our tiered pricing model ensures
          you only pay for what you need, making it easy to scale as your practice grows.
        </p>

        {/* Form */}
        <motion.form
          onSubmit={handleSubmit}
          className="max-w-md space-y-6"
          variants={fadeInVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div>
            <label htmlFor="name" className="block font mb-1">
              Name*
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              className="w-full border border-gray-300 rounded-md px-4 py-2"
            />
          </div>
          <div>
            <label htmlFor="email" className="block font mb-1">
              Email*
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className={`w-full border ${emailInvalid ? "border-red-500" : "border-gray-300"} rounded-md px-4 py-2`}
            />
            {emailInvalid && (
              <p className="text-red-500 text-sm mt-1">
                Please enter a valid email address.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="clinic" className="block font mb-1">
              Clinic/Organization Name
            </label>
            <input
              type="text"
              id="clinic"
              name="clinic"
              className="w-full border border-gray-300 rounded-md px-4 py-2"
            />
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className={`bg-[#427cfc] rounded-3xl font-medium px-6 py-3 text-white transition duration-200 inline-block text-center ${
                loading ? "opacity-70 cursor-not-allowed" : "hover:scale-105 cursor-pointer"
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                    ></path>
                  </svg>
                  Submitting...
                </div>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </motion.form>

        {messageSent && (
          <div className="mt-6 bg-green-100 border border-green-300 text-green-800 text-sm font-medium px-4 py-3 rounded-md shadow-sm">
            ✅ Your message has been sent! We'll be in touch soon.
          </div>
        )}
        {errorMessage && (
          <div className="mt-4 bg-red-100 border border-red-300 text-red-800 text-sm font-medium px-4 py-3 rounded-md shadow-sm">
            ⚠️ {errorMessage}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Pricing;
